/**
 * Gider Pusulası — müşteriden (fatura kesemeyen kişiden) yapılan alımlar.
 *
 * Yalnız HAVALE/EFT ile ödenen alışlar listelenir; pusula tutarı bankadan
 * ödenen kısımdır (bkz. utils/giderPusulasi).
 *
 * Belge üretimi ve GİB'e gönderim HENÜZ YOK: Kolaysoft'ta e-Gider Pusulası
 * servisi var (`ExpenseNoteService`) ama hesabımız yetkili değil, ayrıca
 * stopaj/KDV biçimi mali müşavire soruldu. Bu ekran şimdilik hangi alımlara
 * pusula gerektiğini gösterir; şube elle keserken liste olarak kullanır.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { iv } from './invoiceTheme';
import InvoicePeriodSelector, { ayEtiketi } from './InvoicePeriodSelector';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { giderPusulasiAdaylari, donemeGoreSuz, adayOzeti } from '../../utils/giderPusulasi';
import GiderPusulasiDialog from './GiderPusulasiDialog';

const PAGE_SIZE = 17;
const formatMoney = (v) =>
  Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDay = (g) => (g ? String(g).slice(0, 10).split('-').reverse().join('.') : '—');

function InvoiceExpenseNotePanel({
  islemler = [],
  kesilmisIslemIds = [],
  loading = false,
  month,
  son6 = false,
  tumYil = false,
  onMonth,
  onSon6,
  onTumYil,
  onSave,
  saving = false,
  settings = null,
  sube = '',
  musteriler = null,
  isaretler = {},
  onToggleKesildi = null
}) {
  const [page, setPage] = useState(0);
  const [dialog, setDialog] = useState({ open: false, aday: null });
  // Elle "kesildi" isareti: yanlis tiklamaya karsi her degisiklik onaylatilir.
  const [isaretOnay, setIsaretOnay] = useState(null);
  const [isaretKaydediliyor, setIsaretKaydediliyor] = useState(false);
  const kesildiMi = (a) => Boolean(isaretler?.[a.id]?.kesildi);

  const adaylar = useMemo(() => giderPusulasiAdaylari(islemler, kesilmisIslemIds), [islemler, kesilmisIslemIds]);
  const donem = useMemo(() => {
    if (tumYil) return null;
    return son6 ? null : month;
  }, [month, son6, tumYil]);
  const liste = useMemo(() => (donem ? donemeGoreSuz(adaylar, donem) : adaylar), [adaylar, donem]);
  const ozet = useMemo(() => adayOzeti(liste), [liste]);
  const kesilenAdet = useMemo(() => liste.filter((a) => isaretler?.[a.id]?.kesildi).length, [liste, isaretler]);
  const sayfa = useMemo(() => liste.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE), [liste, page]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25, minHeight: 0, p: 0.5 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: iv.text }}>Gider Pusulası</Typography>
          <Typography sx={{ fontSize: 13, color: iv.textMuted, mt: 0.3 }}>
            Müşteriden yapılan alımlar · yalnız havale/EFT ile ödenenler
          </Typography>
        </Box>
        {onSave ? (
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={() => setDialog({ open: true, aday: null })}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: iv.primary, '&:hover': { bgcolor: iv.primary } }}
          >
            Yeni Pusula
          </Button>
        ) : null}
      </Stack>

      {onMonth ? (
        <Box sx={{ flexShrink: 0 }}>
          <InvoicePeriodSelector
            month={month}
            son6={son6}
            tumYil={tumYil}
            onMonth={onMonth}
            onSon6={onSon6}
            onTumYil={onTumYil}
          />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: iv.text }}>
              {tumYil || son6 ? 'Tüm dönem' : `${ayEtiketi(month)} ayı`}
            </Typography>
            <Chip size="small" label={`${ozet.adet.toLocaleString('tr-TR')} alım`} sx={{ fontWeight: 800 }} />
            <Chip
              size="small"
              label={`${formatMoney(ozet.toplam)} TL`}
              sx={{ fontWeight: 800, bgcolor: iv.primarySoft, color: iv.primary }}
            />
            {ozet.karma > 0 ? (
              <Chip
                size="small"
                label={`${ozet.karma} kısmi ödeme`}
                title="Bir kısmı nakit ödenmiş; pusula yalnız havale tutarı kadar"
                sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }}
              />
            ) : null}
            {onToggleKesildi ? (
              <>
                <Chip
                  size="small"
                  label={`${kesilenAdet} kesildi`}
                  sx={{ fontWeight: 700, bgcolor: '#dcfce7', color: '#166534' }}
                />
                {ozet.adet - kesilenAdet > 0 ? (
                  <Chip
                    size="small"
                    label={`${ozet.adet - kesilenAdet} kesilmedi`}
                    sx={{ fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }}
                  />
                ) : null}
              </>
            ) : null}
            {ozet.musteriEksik > 0 ? (
              <Chip
                size="small"
                label={`${ozet.musteriEksik} müşterisiz`}
                sx={{ fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }}
              />
            ) : null}
          </Stack>
        </Box>
      ) : null}

      <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: 12.5, fontWeight: 600 } }}>
        Pusulalar şimdilik elle kesiliyor. Kolaysoft’ta e-Gider Pusulası hizmeti var ancak hesabımıza henüz
        tanımlı değil; stopaj ve KDV biçimi de mali müşavire soruldu. Cevaplar gelince belge üretimi ve
        gönderim buraya eklenecek.
      </Alert>

      {loading ? (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : liste.length === 0 ? (
        <Alert severity="success" sx={{ '& .MuiAlert-message': { fontWeight: 700, fontSize: 13 } }}>
          Bu dönemde havale/EFT ile ödenmiş alım yok.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', borderColor: iv.border }}>
          <TableContainer sx={{ flex: 1, minHeight: 0 }}>
            <Table size="small" stickyHeader className="iv-gp-table">
              <TableHead>
                <TableRow>
                  {onToggleKesildi ? (
                    <TableCell
                      padding="checkbox"
                      sx={{ fontSize: 12, fontWeight: 800, color: iv.textMuted, bgcolor: '#f8fafc', whiteSpace: 'nowrap' }}
                    >
                      Kesildi
                    </TableCell>
                  ) : null}
                  {['Tarih', 'İşlem', 'Satıcı', 'Ürün', 'Miktar', 'İşlem Tutarı', 'Pusula Tutarı', 'Ödeme'].map((h, i) => (
                    <TableCell
                      key={h}
                      align={i === 5 || i === 6 ? 'right' : 'left'}
                      sx={{ fontSize: 12, fontWeight: 800, color: iv.textMuted, bgcolor: '#f8fafc' }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sayfa.map((a) => (
                  <TableRow
                    key={a.id}
                    hover
                    className="iv-gp-row"
                    onClick={onSave ? () => setDialog({ open: true, aday: a }) : undefined}
                    sx={{
                      ...(onSave ? { cursor: 'pointer' } : {}),
                      ...(kesildiMi(a) ? { bgcolor: '#f0fdf4' } : {})
                    }}
                  >
                    {onToggleKesildi ? (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          color="success"
                          checked={kesildiMi(a)}
                          onChange={() => setIsaretOnay({ aday: a, kesildi: !kesildiMi(a) })}
                          inputProps={{ 'aria-label': 'Pusula kesildi' }}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell sx={{ fontSize: 12.5, fontWeight: 700, color: iv.textMuted }}>{formatDay(a.tarih)}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, fontWeight: 800 }}>{a.islemNo}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, fontWeight: 700 }}>
                      {a.satici || <span style={{ color: iv.danger }}>müşteri kaydı yok</span>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{a.urun}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: iv.textMuted }}>
                      {a.gram > 0 ? `${a.gram} gr` : `${a.adet} adet`}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 700, color: iv.textMuted, whiteSpace: 'nowrap' }}>
                      {formatMoney(a.islemTutari)} ₺
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 800, color: iv.primary, whiteSpace: 'nowrap' }}>
                      {formatMoney(a.pusulaTutari)} ₺
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={a.odeme === 'karma' ? 'Kısmi' : 'Havale'}
                        sx={
                          a.odeme === 'karma'
                            ? { fontWeight: 700, height: 20, bgcolor: '#fef3c7', color: '#92400e' }
                            : { fontWeight: 700, height: 20, bgcolor: '#dbeafe', color: '#1d4ed8' }
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={liste.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            sx={{ borderTop: `1px solid ${iv.border}`, '& .MuiTablePagination-displayedRows': { fontSize: 12, fontWeight: 700 } }}
          />
        </Paper>
      )}
      <Dialog open={Boolean(isaretOnay)} onClose={() => !isaretKaydediliyor && setIsaretOnay(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {isaretOnay?.kesildi ? 'Pusula kesildi mi?' : 'İşaret kaldırılsın mı?'}
        </DialogTitle>
        <DialogContent>
          {isaretOnay ? (
            <Box sx={{ fontSize: 14 }}>
              <Typography sx={{ fontSize: 14, mb: 1.5 }}>
                {isaretOnay.kesildi
                  ? 'Bu alışın gider pusulasını koçandan kestiniz mi? Kesildi olarak işaretlenecek.'
                  : 'Bu alış KESİLDİ olarak işaretli. Kesilmemiş olarak mı işaretlemek istiyorsunuz?'}
              </Typography>
              <Box sx={{ p: 1.25, borderRadius: '8px', bgcolor: '#f8fafc', border: `1px solid ${iv.border}` }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                  {isaretOnay.aday.satici || 'müşteri kaydı yok'} · İşlem {isaretOnay.aday.islemNo}
                </Typography>
                <Typography sx={{ fontSize: 13, color: iv.textMuted }}>
                  {formatDay(isaretOnay.aday.tarih)} · {isaretOnay.aday.urun} · pusula tutarı{' '}
                  {formatMoney(isaretOnay.aday.pusulaTutari)} ₺
                </Typography>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsaretOnay(null)} disabled={isaretKaydediliyor} sx={{ textTransform: 'none' }}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            disableElevation
            color={isaretOnay?.kesildi ? 'success' : 'warning'}
            disabled={isaretKaydediliyor}
            onClick={async () => {
              setIsaretKaydediliyor(true);
              try {
                await onToggleKesildi(isaretOnay.aday, isaretOnay.kesildi);
                setIsaretOnay(null);
              } finally {
                setIsaretKaydediliyor(false);
              }
            }}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {isaretOnay?.kesildi ? 'Evet, kesildi' : 'Evet, kesilmedi olarak işaretle'}
          </Button>
        </DialogActions>
      </Dialog>
      <GiderPusulasiDialog
        open={dialog.open}
        aday={dialog.aday}
        saving={saving}
        settings={settings}
        sube={sube}
        musteriler={musteriler}
        onClose={() => setDialog({ open: false, aday: null })}
        onSave={async (form) => {
          await onSave(form, dialog.aday);
          setDialog({ open: false, aday: null });
        }}
      />
    </Box>
  );
}

export default InvoiceExpenseNotePanel;
